/**
 * imageInfo.js — ПРОВОДКА РАЗДЕЛА «ИНФОРМАЦИЯ» ЛЕВОЙ ПАНЕЛИ. [12.0.25]
 *
 * Строки собирает imageInfoCore.js (чистый модуль, под тестами). Здесь —
 * откуда брать числа и когда их писать:
 *
 *   · показатьСведенияСерии(series) — при открытии серии: пациент,
 *     исследование, серия, шаг, матрица, и заодно текущий срез;
 *   · строка «Изображение» — при каждом листании, но только для ОДНОГО окна:
 *     того, над которым курсор, а пока курсор не заходил ни в одно — для
 *     активного (или первого). Правило то же, что у верхней надписи
 *     «Срез: …» (viewport.js, updGlobal). Иначе строку дёргали бы все окна
 *     разом: общий бегунок листает все 2D-окна, а нативная стопка следует
 *     за реформатом и шлёт свои события.
 *   · сброситьСведения() — при закрытии исследования.
 *
 * ОТКУДА ЧИСЛА — И ПОЧЕМУ НЕ ИЗ imagePlaneModule. Загрузчик ради геометрии
 * подставляет значения, когда тега в файле нет: шаг пикселя [1, 1], позицию
 * «номер × толщина», ориентацию аксиальную (loaderManager, loaderWorkerBridge
 * — там это правильно: реформаты не терпят пустоты). Показать такие числа
 * врачу — значит выдать выдумку за снимок: «шаг между срезами» из выдуманных
 * позиций совпал бы с толщиной, а это ровно та подмена, от которой строка и
 * должна защищать. Поэтому шаг пикселя и признак «позиции настоящие» берутся
 * из самого файла первого кадра — кэш dataSet или naturalized-метаданные.
 *
 * EXPORTS: показатьСведенияСерии, сброситьСведения
 */

import { cornerstone } from '../core/init.js';
import { getRenderingEngine, getActiveOrFirstViewportId } from '../core/viewportState.js';
import { getCachedDataSet } from '../loaders/loaderManager.js';
import { formatPatientName } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { ИДЫ, ПОРЯДОК, сведенияСерии, строкаИзображения, пустыеСведения } from './imageInfoCore.js';

/** Окно, чей срез описывает строка «Изображение». null — ещё не выбрано. */
let _окно = null;
let _привязано = false;

/** Записать строку в раздел и в мобильный ящик (там те же id с «-m»). */
function _записать(ключ, текст) {
  for (const суффикс of ['', '-m']) {
    const эл = document.getElementById(ИДЫ[ключ] + суффикс);
    if (эл && эл.textContent !== текст) эл.textContent = текст;
  }
}

/** Значение тега: сперва сам файл (dataSet), затем naturalized-метаданные. */
function _тег(ds, nat, hex, kw) {
  try { const v = ds?.string?.(hex); if (v && v.trim()) return v.trim(); } catch (e) { /* нет в кэше */ }
  let v = nat ? nat[kw] : undefined;
  if (v == null) return '';
  if (Array.isArray(v)) v = v.join('\\');
  return String(v).trim();
}

const _вектор = (s) => {
  const ч = String(s || '').split('\\').map(Number);
  return ч.length && ч.every(Number.isFinite) ? ч : null;
};

/** Двоичное поле US (Rows, Columns): только uint16 — строкой его не прочесть. */
function _us(ds, nat, hex, kw) {
  try { const v = ds?.uint16?.(hex); if (Number.isFinite(v) && v > 0) return v; } catch (e) { /* нет поля */ }
  const v = Number(Array.isArray(nat?.[kw]) ? nat[kw][0] : nat?.[kw]);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

function _номерDicom(imageId) {
  if (!imageId) return undefined;
  try {
    const nat = cornerstone.metaData.get('instance', imageId);
    const v = parseInt(Array.isArray(nat?.InstanceNumber) ? nat.InstanceNumber[0] : nat?.InstanceNumber, 10);
    if (Number.isFinite(v)) return v;
  } catch (e) { /* нет метаданных */ }
  try {
    const v = parseInt(cornerstone.metaData.get('generalImageModule', imageId)?.instanceNumber, 10);
    if (Number.isFinite(v)) return v;
  } catch (e) { /* нет метаданных */ }
  return undefined;
}

/** Что сейчас стоит в окне — по состоянию самого окна, без событий. */
function _срезОкна(vp) {
  if (!vp) return null;
  if (String(vp.type || '').toLowerCase().includes('3d')) return { объём3D: true };
  if (typeof vp.setImageIdIndex === 'function') {
    // Стопка исходных кадров
    let ids = [], i;
    try { ids = vp.getImageIds?.() || []; } catch (e) { ids = []; }
    try { i = vp.getCurrentImageIdIndex?.(); } catch (e) { i = undefined; }
    if (!Number.isFinite(i) || !ids.length) return null;
    return { номер: i + 1, всего: ids.length, номерDicom: _номерDicom(ids[i]) };
  }
  // Реформат: тот же порядок вызовов, что в globalSliceSlider.js
  let i, n;
  try { i = vp.getCurrentImageIdIndex?.(); } catch (e) { i = undefined; }
  if (!Number.isFinite(i)) { try { i = vp.getSliceIndex?.(); } catch (e) { i = undefined; } }
  try { n = vp.getNumberOfSlices?.(); } catch (e) { n = undefined; }
  if (!Number.isFinite(i)) return null;
  return { номер: i + 1, всего: n, реформат: true };
}

function _показатьСрезОкна(vpId) {
  let vp = null;
  try { vp = vpId ? getRenderingEngine()?.getViewport?.(vpId) : null; } catch (e) { vp = null; }
  _записать('изображение', строкаИзображения(_срезОкна(vp) || {}));
}

/** Событие пришло от «нашего» окна? Окно под курсором становится нашим. */
function _моёОкно(ev, vpId) {
  if (!vpId) return false;
  if (ev.target?.matches?.(':hover')) { _окно = vpId; return true; }
  const наше = _окно || getActiveOrFirstViewportId();
  return vpId === наше;
}

function _привязать() {
  if (_привязано) return;
  const контейнер = document.getElementById('viewport-container');
  const E = cornerstone?.Enums?.Events;
  if (!контейнер || !E) return;
  _привязано = true;

  контейнер.addEventListener(E.STACK_NEW_IMAGE, (ev) => {
    const d = ev.detail || {};
    if (!_моёОкно(ev, d.viewportId)) return;
    let всего;
    try { всего = getRenderingEngine()?.getViewport?.(d.viewportId)?.getImageIds?.()?.length; } catch (e) { всего = undefined; }
    _записать('изображение', строкаИзображения({
      номер: (d.imageIdIndex ?? 0) + 1, всего, номерDicom: _номерDicom(d.imageId),
    }));
  }, true);

  контейнер.addEventListener(E.VOLUME_NEW_IMAGE, (ev) => {
    const d = ev.detail || {};
    if (!_моёОкно(ev, d.viewportId)) return;
    let vp = null;
    try { vp = getRenderingEngine()?.getViewport?.(d.viewportId); } catch (e) { vp = null; }
    if (String(vp?.type || '').toLowerCase().includes('3d')) return;
    _записать('изображение', строкаИзображения({
      номер: (d.imageIndex ?? d.sliceIndex ?? 0) + 1, всего: d.numberOfSlices, реформат: true,
    }));
  }, true);

  // Курсор зашёл в другое окно — строка сразу показывает ЕГО срез, не
  // дожидаясь прокрутки. Окно узнаём по data-viewport-uid, который ставит
  // сам Cornerstone (так же ищут окно measureConditions.js и advancedUI.js).
  контейнер.addEventListener('pointerover', (e) => {
    const эл = e.target?.closest?.('[data-viewport-uid]');
    const id = эл?.getAttribute?.('data-viewport-uid');
    if (!id || id === _окно) return;
    _окно = id;
    _показатьСрезОкна(id);
  }, { passive: true });
}

/**
 * Заполнить раздел по серии. Зовётся из updateOverlays — только для
 * основного показа, не для окон сравнения (см. там же).
 * @param {object} series — запись серии из загрузчика
 */
export function показатьСведенияСерии(series) {
  if (!series) return;
  _привязать();
  try {
    const кадры = series.images || series.files || [];
    const первый = кадры[0]?.imageId;
    let ds = null, nat = null;
    try { ds = первый ? getCachedDataSet(первый) : null; } catch (e) { ds = null; }
    try { nat = первый ? cornerstone.metaData.get('instance', первый) : null; } catch (e) { nat = null; }

    // Позиции — только если у первого кадра тег позиции есть В ФАЙЛЕ. Нет
    // тега — загрузчик подставил «номер × толщина» (см. шапку модуля).
    const естьПозиция = !!_тег(ds, nat, 'x00200032', 'ImagePositionPatient');
    const ориентацияФайла = _вектор(_тег(ds, nat, 'x00200037', 'ImageOrientationPatient'));
    const позиции = [];
    let ориентация = ориентацияФайла && ориентацияФайла.length === 6 ? ориентацияФайла : null;
    if (естьПозиция && ориентация) {
      for (const к of кадры) {
        let ipp = к.ipp;
        if (!Array.isArray(ipp) && к.imageId) {
          try { ipp = cornerstone.metaData.get('imagePlaneModule', к.imageId)?.imagePositionPatient; }
          catch (e) { ipp = null; }
        }
        if (Array.isArray(ipp)) позиции.push(ipp);
      }
    } else {
      ориентация = null;
    }

    const строки = сведенияСерии({
      пациент: formatPatientName(series.patientName),
      описаниеИсследования: series.studyDesc || _тег(ds, nat, 'x00081030', 'StudyDescription'),
      датаИсследования: series.studyDate || _тег(ds, nat, 'x00080020', 'StudyDate'),
      описаниеСерии: series.seriesDesc,
      номерСерии: _тег(ds, nat, 'x00200011', 'SeriesNumber'),
      модальность: series.modality,
      строк: Number(series.rows) > 0 ? series.rows : _us(ds, nat, 'x00280010', 'Rows'),
      столбцов: Number(series.columns) > 0 ? series.columns : _us(ds, nat, 'x00280011', 'Columns'),
      пиксель: _вектор(_тег(ds, nat, 'x00280030', 'PixelSpacing')),
      позиции,
      ориентация,
      интервалТег: parseFloat(_тег(ds, nat, 'x00180088', 'SpacingBetweenSlices')),
      кадров: кадры.length,
    });
    for (const ключ of ПОРЯДОК) if (строки[ключ]) _записать(ключ, строки[ключ]);
  } catch (e) {
    log.debug('[Информация] сведения серии:', e.message);
  }
  _показатьСрезОкна(_окно || getActiveOrFirstViewportId());
}

/** Все строки прочерками — при закрытии исследования. */
export function сброситьСведения() {
  const пусто = пустыеСведения();
  for (const ключ of ПОРЯДОК) _записать(ключ, пусто[ключ]);
}
